import { useEffect, useRef } from 'react';

// Keep enough rendered content ahead to cover a couple of viewport scrolls.
export const getPrefetchDistance = height => Math.max(1200, height * 2);

export default function useReaderScroll({
    containerRef, contentRef, scrollAdjustmentRef, articles, currentArticleIndex,
    loadNextArticle, onArticleChange, isLoading, error,
}) {
    const latest = useRef(null);
    const scheduleRef = useRef(null);
    latest.current = { currentArticleIndex, loadNextArticle, onArticleChange, isLoading, error };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;
        let frame = null;
        let trackSelection = false;

        const measure = () => {
            frame = null;
            const state = latest.current;
            const sections = container.querySelectorAll('.article-section');
            const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;
            if (sections.length && container.clientHeight > 0 && !state.isLoading && !state.error &&
                remaining <= getPrefetchDistance(container.clientHeight)) {
                state.loadNextArticle();
            }

            // Only user scrolling changes the active article, not a resize or batch append.
            if (trackSelection) {
                const top = container.getBoundingClientRect().top;
                let best = Infinity;
                let index = state.currentArticleIndex;
                sections.forEach(section => {
                    const distance = Math.abs(section.getBoundingClientRect().top - top);
                    if (distance < best) {
                        best = distance;
                        index = Number(section.getAttribute('data-index'));
                    }
                });
                if (index !== state.currentArticleIndex) state.onArticleChange?.(index);
            }
            trackSelection = false;
        };
        const schedule = () => {
            if (frame === null) frame = window.requestAnimationFrame(measure);
        };
        const onScroll = () => {
            const adjustment = scrollAdjustmentRef?.current;
            const corrected = adjustment != null && Math.abs(adjustment - container.scrollTop) < 1;
            if (scrollAdjustmentRef) scrollAdjustmentRef.current = null;
            trackSelection = !corrected;
            schedule();
        };
        scheduleRef.current = schedule;
        container.addEventListener('scroll', onScroll, { passive: true });
        const observer = new ResizeObserver(schedule);
        observer.observe(container);
        if (contentRef.current) observer.observe(contentRef.current);
        schedule();
        return () => {
            container.removeEventListener('scroll', onScroll);
            observer.disconnect();
            window.cancelAnimationFrame(frame);
            scheduleRef.current = null;
        };
    }, [containerRef, contentRef, scrollAdjustmentRef]);

    // Continue filling the buffer even when a short batch doesn't cause a scroll event.
    useEffect(() => {
        scheduleRef.current?.();
    }, [articles, isLoading, error]);
}
